import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import Analysisapi from "@/api/analysis";
import moment from "moment";
import styles from "./style.module.css";

// 水质类型映射
const waterTypes = [
  { key: "COD", type: 1, color: "#5470C6" },
  { key: "NH3-N", type: 3, color: "#91CC75" },
  { key: "TP", type: 4, color: "#FAC858" },
  { key: "TN", type: 2, color: "#EE6666" },
  { key: "PH", type: 7, color: "#73C0DE" },
];
const mockPie3 = [
  { value: 30, name: "待完成" },
  { value: 70, name: "进行中" },
  { value: 100, name: "已完成" },
];

export default function Performance() {
  const lineRef = useRef(null);
  const pie1Ref = useRef(null);
  const pie2Ref = useRef(null);
  const pie3Ref = useRef(null);
  const [alarmData, setAlarmData] = useState({
    total: 0,
    undo: 0,
    doing: 0,
    done: 0,
  });
  const [defaultOrderData, setDefaultOrderData] = useState({
    total: 0,
    undo: 0,
    doing: 0,
    done: 0,
  });
  const [waterStat, setWaterStat] = useState({
    SSLL: "-",
    dayLJLL: "-",
    monthLJLL: "-",
    yearLJLL: "-",
  });
  const [workOrderData, setWorkOrderData] = useState({});

  // 出水水质趋势
  const fetchWaterQuality = async () => {
    const end = moment().endOf("month").format("YYYY-MM-DD 23:59:59");
    const start = moment().startOf("month").format("YYYY-MM-DD 00:00:00");
    const params = {
      queryTimeStartStr: start,
      queryTimeEndStr: end,
      reportType: 2, // 月报表
      inOrOut: 2, // 出水
      cod: 1,
      nh3n: 3,
      tn: 2,
      tp: 4,
      stationId: "61",
    };
    try {
      const res = await Analysisapi.selectWaterQuality(params);
      if (res?.status === "0" && res.data && res.data.data) {
        // 组装x轴和多条线
        const xSet = new Set();
        const series = [];
        // 先收集所有时间点
        waterTypes.forEach((wt) => {
          const arr = res.data.data[wt.type] || [];
          arr.forEach((item) => xSet.add(item.createTime.slice(0, 10)));
        });
        const xAxis = Array.from(xSet).sort();
        // 每条线
        waterTypes.forEach((wt) => {
          const arr = res.data.data[wt.type] || [];
          // 按x轴顺序补齐
          const map = {};
          arr.forEach((item) => {
            map[item.createTime.slice(0, 10)] = Number(item.value);
          });
          const data = xAxis.map((x) => map[x] ?? null);
          series.push({
            name: wt.key,
            type: "line",
            data,
            smooth: true,
            showSymbol: false,
            color: wt.color,
          });
        });
        if (lineRef.current) {
          const chart = echarts.init(lineRef.current);
          chart.setOption({
            tooltip: { trigger: "axis" },
            legend: { data: waterTypes.map((w) => w.key) },
            xAxis: { type: "category", data: xAxis },
            yAxis: { type: "value" },
            series,
            grid: { top: 40, left: 40, right: 20, bottom: 30 },
          });
        }
      }
    } catch {
      // 空数据
      if (lineRef.current) {
        const chart = echarts.init(lineRef.current);
        chart.clear();
      }
    }
  };

  // 年处理量/水量统计
  const fetchWaterStat = async () => {
    try {
      const res = await Analysisapi.outWater({});
      if (res?.status === "0" && res.data) {
        setWaterStat(res.data);
      }
    } catch {
      setWaterStat({ SSLL: "-", dayLJLL: "-", monthLJLL: "-", yearLJLL: "-" });
    }
  };

  // 报警事件统计
  const fetchAlarm = async () => {
    try {
      const res = await Analysisapi.totalAlarmNew();
      if (res?.status === "0" && res.data) {
        setAlarmData(res.data);
        if (pie1Ref.current) {
          const chart = echarts.init(pie1Ref.current);
          chart.setOption({
            tooltip: { trigger: "item" },
            series: [
              {
                type: "pie",
                radius: ["60%", "75%"],
                label: { show: true, position: "outside" },
                labelLayout: {
                  hideOverlap: false, // 不隐藏重叠标签（配合容器留白）
                },
                data: [
                  { value: res.data.undo, name: "未完成" },
                  { value: res.data.doing, name: "处理中" },
                  { value: res.data.done, name: "已完成" },
                ],
              },
            ],
          });
        }
      }
    } catch {
      setAlarmData({ total: 0, undo: 0, doing: 0, done: 0 });
    }
  };

  // 缺陷单统计
  const fetchDefaultOrder = async () => {
    try {
      const res = await Analysisapi.getDefectOrderStatistics({
        stationId: "61",
        startTime: "1999-01-01",
        endTime: "3000-01-01",
      });
      if (res?.status === "0" && res.data) {
        setDefaultOrderData(res.data);
        if (pie2Ref.current) {
          const chart = echarts.init(pie2Ref.current);
          chart.setOption({
            tooltip: { trigger: "item" },
            series: [
              {
                type: "pie",
                radius: ["60%", "75%"],
                label: { show: true, position: "outside" },
                labelLayout: {
                  hideOverlap: false, // 不隐藏重叠标签（配合容器留白）
                },
                data: [
                  { value: res.data.unhandleNumber, name: "未处理" },
                  { value: res.data.handlingNumber, name: "处理中" },
                  { value: res.data.timeoutNumber, name: "超时" },
                  { value: res.data.finishNumber, name: "已完成" },
                ],
              },
            ],
          });
        }
      }
    } catch {
      setDefaultOrderData({ total: 0, undo: 0, doing: 0, done: 0 });
    }
  };

  // 工单统计
  const renderWorkOrderPie = async () => {
    const res = await Analysisapi.getPatrolWorkOrderStatistics({
      stationId: "61",
      startTime: "1999-01-01",
      endTime: "3000-01-01",
    });
    if (res?.status === "0" && res.data) {
      setWorkOrderData(res.data);
      if (pie3Ref.current) {
        const chart = echarts.init(pie3Ref.current);
        chart.setOption({
          tooltip: { trigger: "item" },
          series: [
            {
              type: "pie",
              radius: ["60%", "75%"],
              label: { show: true, position: "outside" },
              labelLayout: {
                hideOverlap: false, // 不隐藏重叠标签（配合容器留白）
              },
              data: [
                { value: res.data.unhandleNumber, name: "未处理" },
                { value: res.data.handlingNumber, name: "处理中" },
                { value: res.data.timeoutNumber, name: "超时" },
                { value: res.data.finishNumber, name: "已完成" },
              ],
            },
          ],
        });
      }
    }
  };

  useEffect(() => {
    fetchWaterQuality();
    fetchWaterStat();
    fetchAlarm();
    fetchDefaultOrder();
    renderWorkOrderPie();
    // 销毁
    return () => {
      [lineRef, pie1Ref, pie2Ref, pie3Ref].forEach((ref) => {
        if (ref.current) {
          echarts.dispose(ref.current);
        }
      });
    };
  }, []);

  return (
    <div style={{ background: "#fff", padding: 24 }}>
      <h2 style={{ textAlign: "center", marginBottom: 24 }}>
        绵阳经开区智慧化循环利用污水处理厂
      </h2>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <span>
          日处理量: <b>{waterStat.dayLJLL ?? "-"}</b> m³
        </span>
        <span>
          月累计量: <b>{waterStat.monthLJLL ?? "-"}</b> m³
        </span>
        <span>
          进水瞬时流量: <b>{waterStat.SSLL ?? "-"}</b> m³/h
        </span>
        <span>
          设计负荷率 <b>{waterStat.designLoad}</b>
        </span>
      </div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontWeight: "bold", marginBottom: 8 }}>出水水质趋势</div>
        <div style={{ display: "flex" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div ref={lineRef} style={{ width: "100%", height: 220 }} />
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 16 }}>
        <div
          style={{
            flex: 1,
            background: "#fafafa",
            padding: 16,
            borderRadius: 8,
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 8 }}>
            报警事件统计
          </div>
          <div ref={pie1Ref} style={{ width: "100%", height: 200 }} />
          <div className={styles["chart-footer"]}>
            总数 <strong>{alarmData.total}</strong> 未完成{" "}
            <strong>{alarmData.undo}</strong> 处理中{" "}
            <strong>{alarmData.doing}</strong> 已完成{" "}
            <strong>{alarmData.done}</strong>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            background: "#fafafa",
            padding: 16,
            borderRadius: 8,
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 8 }}>缺陷单统计</div>
          <div ref={pie2Ref} style={{ width: "100%", height: 200 }} />
          <div className={styles["chart-footer"]}>
            缺陷单总数{" "}
            <strong style={{ paddingRight: 5 }}>
              {defaultOrderData.totalNumber}
            </strong>
            未处理{" "}
            <strong style={{ paddingRight: 5 }}>
              {defaultOrderData.unhandleNumber}
            </strong>
            处理中{" "}
            <strong style={{ paddingRight: 5 }}>
              {defaultOrderData.handlingNumber}
            </strong>
            超时{" "}
            <strong style={{ paddingRight: 5 }}>
              {defaultOrderData.timeoutNumber}
            </strong>
            已完成 <strong>{defaultOrderData.finishNumber}</strong>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            background: "#fafafa",
            padding: 16,
            borderRadius: 8,
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 8 }}>工单统计</div>
          <div ref={pie3Ref} style={{ width: "100%", height: 200 }} />
          <div className={styles["chart-footer"]}>
            工单总数{" "}
            <strong style={{ paddingRight: 5 }}>
              {workOrderData.totalNumber}
            </strong>
            未处理{" "}
            <strong style={{ paddingRight: 5 }}>
              {workOrderData.unhandleNumber}
            </strong>
            处理中{" "}
            <strong style={{ paddingRight: 5 }}>
              {workOrderData.handlingNumber}
            </strong>
            超时{" "}
            <strong style={{ paddingRight: 5 }}>
              {workOrderData.timeoutNumber}
            </strong>
            已完成 <strong>{workOrderData.finishNumber}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
